import axios from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

export const partnerHttp = axios.create({
  baseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

partnerHttp.interceptors.request.use((config) => {
  const token = localStorage.getItem('partner_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

partnerHttp.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('partner_token');
      localStorage.removeItem('partner_user');
      const path = window.location.pathname;
      const publicPartnerAuth =
        path.startsWith('/partner/login') ||
        path.startsWith('/partner/register') ||
        path.startsWith('/partner/forgot-password') ||
        path.startsWith('/partner/reset-password');
      if (!publicPartnerAuth) {
        window.location.href = '/partner/login';
      }
    }
    return Promise.reject(error);
  }
);

const adminHttp = axios.create({ baseURL, timeout: 30000 });
adminHttp.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export type PartnerAnalyticsReport = {
  from: string;
  to: string;
  hospitalIds: string[];
  summary: {
    activePartners: number;
    newPartnerLinksInRange: number;
    referralLeads: number;
    opdConversions: number;
    ipdConversions: number;
    leadToOpdPct: number | null;
    opdToIpdPct: number | null;
  };
  partnersAddedByDay: Array<{ date: string; count: number }>;
  leadsByDay: Array<{ date: string; count: number }>;
  conversionsByDay: Array<{ date: string; opd: number; ipd: number }>;
  topPartners: Array<{
    partnerId: string;
    name: string;
    leads: number;
    opd: number;
    ipd: number;
    points: number;
  }>;
};

export const referralPartnerApi = {
  publicHospitals: () => partnerHttp.get('/api/referral-partner/public/hospitals'),
  register: (body: Record<string, unknown>) =>
    partnerHttp.post('/api/referral-partner/auth/register', body),
  login: (email: string, password: string) =>
    partnerHttp.post('/api/referral-partner/auth/login', { email, password }),
  forgotPassword: (email: string) =>
    partnerHttp.post('/api/referral-partner/auth/forgot-password', { email }),
  resetPassword: (token: string, newPassword: string) =>
    partnerHttp.post('/api/referral-partner/auth/reset-password', { token, newPassword }),
  me: () => partnerHttp.get('/api/referral-partner/portal/me'),
  updateProfile: (body: Record<string, unknown>) =>
    partnerHttp.patch('/api/referral-partner/portal/me', body),
  hospitals: () => partnerHttp.get('/api/referral-partner/portal/hospitals'),
  availableHospitals: () => partnerHttp.get('/api/referral-partner/portal/available-hospitals'),
  leads: (hospitalId?: string) =>
    partnerHttp.get('/api/referral-partner/portal/leads', {
      params: hospitalId ? { hospitalId } : {},
    }),
  createLead: (body: Record<string, unknown>) =>
    partnerHttp.post('/api/referral-partner/portal/leads', body),
  points: () => partnerHttp.get('/api/referral-partner/portal/points'),
  payouts: () => partnerHttp.get('/api/referral-partner/portal/payouts'),
  requestPayout: (body: { hospitalId: string; ledgerIds: string[]; partnerNotes?: string }) =>
    partnerHttp.post('/api/referral-partner/portal/payouts/request', body),

  admin: {
    partners: () => adminHttp.get('/api/referral-partner/admin/partners'),
    approve: (id: string, hospitalIds?: string[]) =>
      adminHttp.post(`/api/referral-partner/admin/partners/${id}/approve`, { hospitalIds }),
    assignHospitals: (id: string, hospitalIds: string[]) =>
      adminHttp.post(`/api/referral-partner/admin/partners/${id}/hospitals`, { hospitalIds }),
    suspend: (id: string) => adminHttp.post(`/api/referral-partner/admin/partners/${id}/suspend`),
    payouts: (status?: string) =>
      adminHttp.get('/api/referral-partner/admin/payouts', { params: status ? { status } : {} }),
    approvePayout: (id: string, adminNotes?: string) =>
      adminHttp.post(`/api/referral-partner/admin/payouts/${id}/approve`, { adminNotes }),
    markPaid: (id: string, paymentReference: string) =>
      adminHttp.post(`/api/referral-partner/admin/payouts/${id}/paid`, { paymentReference }),
    rejectPayout: (id: string, adminNotes?: string) =>
      adminHttp.post(`/api/referral-partner/admin/payouts/${id}/reject`, { adminNotes }),
    setPayoutRate: (partnerReferralRupeePerPoint: number, hospitalId?: string) =>
      adminHttp.patch('/api/referral-partner/admin/hospital-payout-rate', {
        partnerReferralRupeePerPoint,
        hospitalId,
      }),

    super: {
      pendingApprovals: () => adminHttp.get('/api/referral-partner/admin/super/pending-approvals'),
      exportHospitalReport: (hospitalId: string, from?: string, to?: string) =>
        adminHttp.get('/api/referral-partner/admin/super/report/export', {
          params: { hospitalId, from, to },
          responseType: 'blob',
        }),
      exportHospitalDetailedCsv: (hospitalId: string, from?: string, to?: string) =>
        adminHttp.get('/api/referral-partner/admin/super/report/export-detailed-csv', {
          params: { hospitalId, from, to },
          responseType: 'blob',
        }),
      approvePartner: (id: string) =>
        adminHttp.post(`/api/referral-partner/admin/super/partners/${id}/approve`),
      approveRate: (id: string) =>
        adminHttp.post(`/api/referral-partner/admin/super/rates/${id}/approve`),
      rejectRate: (id: string, rejectionReason?: string) =>
        adminHttp.post(`/api/referral-partner/admin/super/rates/${id}/reject`, { rejectionReason }),
      approveLeadPoints: (id: string) =>
        adminHttp.post(`/api/referral-partner/admin/super/lead-points/${id}/approve`),
      rejectLeadPoints: (id: string, rejectionReason?: string) =>
        adminHttp.post(`/api/referral-partner/admin/super/lead-points/${id}/reject`, { rejectionReason }),
    },

    chain: {
      pendingApprovals: () => adminHttp.get('/api/referral-partner/admin/chain/pending-approvals'),
      approvePartner: (id: string) =>
        adminHttp.post(`/api/referral-partner/admin/chain/partners/${id}/approve`),
      approveRate: (id: string) =>
        adminHttp.post(`/api/referral-partner/admin/chain/rates/${id}/approve`),
      rejectRate: (id: string, rejectionReason?: string) =>
        adminHttp.post(`/api/referral-partner/admin/chain/rates/${id}/reject`, { rejectionReason }),
      approveLeadPoints: (id: string) =>
        adminHttp.post(`/api/referral-partner/admin/chain/lead-points/${id}/approve`),
      rejectLeadPoints: (id: string, rejectionReason?: string) =>
        adminHttp.post(`/api/referral-partner/admin/chain/lead-points/${id}/reject`, { rejectionReason }),
    },

    analytics: (params: { from?: string; to?: string; hospitalId?: string }) =>
      adminHttp.get('/api/referral-partner/admin/analytics', { params }),

    hospital: {
      pendingApprovals: () => adminHttp.get('/api/referral-partner/admin/hospital/pending-approvals'),
      partners: () => adminHttp.get('/api/referral-partner/admin/hospital/partners'),
      partnerRates: (partnerId: string) =>
        adminHttp.get('/api/referral-partner/admin/hospital/partner-rates', { params: { partnerId } }),
      partnerLeads: (partnerId?: string) =>
        adminHttp.get('/api/referral-partner/admin/hospital/partner-leads', {
          params: partnerId ? { partnerId } : {},
        }),
      exportReport: (from?: string, to?: string) =>
        adminHttp.get('/api/referral-partner/admin/hospital/report/export', {
          params: { from, to },
          responseType: 'blob',
        }),
      exportDetailedCsv: (from?: string, to?: string) =>
        adminHttp.get('/api/referral-partner/admin/hospital/report/export-detailed-csv', {
          params: { from, to },
          responseType: 'blob',
        }),
      requestRateChange: (body: {
        partnerId: string;
        milestone: 'NEW' | 'OPD_VISITED' | 'IPD_ADMITTED';
        requestedPoints: number;
        reason?: string;
      }) => adminHttp.post('/api/referral-partner/admin/hospital/rate-requests', body),
      requestLeadPoints: (body: {
        leadId: string;
        milestone: 'NEW' | 'OPD_VISITED' | 'IPD_ADMITTED';
        requestedPoints: number;
        reason?: string;
      }) => adminHttp.post('/api/referral-partner/admin/hospital/lead-point-requests', body),
    },
  },
};
